import {
  FACTIONS, FATIGUE_MAX, SHRINE_POWER_MAX, type ClassId, type Faction,
} from '../core/constants.ts';
import { CLASSES } from '../core/classes.ts';

/**
 * Player names go into markup, and a player names themselves.
 *
 * Nothing malicious can reach here in a local match, but a name is the one
 * string in this HUD that does not come from the codebase, and it will be coming
 * off a network before long.
 */
/**
 * What each innate answers to.
 *
 * Shown on the tile because a spell you cannot see the key for is a spell nobody
 * presses — and Flight in particular exists on the bar only to be legible, since
 * it is held rather than cast.
 */
const INNATE_KEY: Record<string, string> = {
  call_ghost: 'G',
  resurrect: 'R',
  flight: 'SPC',
};

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}
import { canFactionRespawn, poolReadout, poolUnderfoot } from '../core/arena.ts';
import {
  canHear, canSpeak, makeChatLog, sanitiseChat,
  type ChatMessage, type ChatScope,
} from '../core/chat.ts';
import type { MatchPhase } from '../core/match.ts';
import { GENERAL_KEYS, GHOST_KEYS, PRIMERS, type Key } from './primer.ts';

/**
 * How many lines of chat stay on screen.
 *
 * Fewer than the log keeps, because the log is a record and this is a corner of
 * a screen somebody is fighting in. Anything older has been read or has not.
 */
const CHAT_SHOWN = 8;
import { formatClock } from '../core/match.ts';
import { levelProgress } from '../core/progression.ts';
import { isEmbodied, tierInSlot, type PlayerState } from '../core/player.ts';
import { slotUnlockLevel, tierForSpell } from '../core/progression.ts';
import { SPELLS_BY_ID, slotLabel, GLOBAL_COOLDOWN, displayName, innatesFor, type SpellDef } from '../core/spells.ts';
import { rosterOf, type SimEvent, type SimState } from '../core/sim.ts';
import { badgesFor } from './statusIcons.ts';

const ORDINAL_SUFFIX = (n: number): string => {
  if (n % 100 >= 11 && n % 100 <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
};

const ordinal = (n: number): string => `${n}${ORDINAL_SUFFIX(n)}`;

/**
 * Level and class in four characters: `7cl`, `12me`.
 *
 * Both halves matter and neither is guessable from the other. Class says what
 * somebody is *for* — whether that name across the room heals, holds pools or
 * lays traps — and level says how much of it they can do, which against an enemy
 * is the difference between a nuisance and a problem. It goes on both rosters
 * for the same reason: knowing you are looking at a twelfth-level Cleric is what
 * decides whether you commit.
 */
// Takes anything carrying a level and a class, so it serves a player and a
// roster entry alike — the enemy list is built from the latter.
function badge(p: { level: number; cls: string }): string {
  return `${p.level}${CLASSES[p.cls as ClassId].name.slice(0, 2).toLowerCase()}`;
}

const el = <T extends HTMLElement>(sel: string): T => {
  const found = document.querySelector<T>(sel);
  if (!found) throw new Error(`HUD is missing ${sel}`);
  return found;
};

interface LogLine { text: string; kind: string; at: number }

export class Hud {
  private log: LogLine[] = [];
  private statusSignature = '';
  private bannerUntil = 0;
  /** Short-lived line above the crosshair saying why a cast did nothing. */
  private notice = '';
  private noticeUntil = 0;
  private banner = { title: '', sub: '' };
  private lastXp = 0;
  private sessionXp = 0;

  private readonly nameEl = el('.hud-name');
  private readonly titleEl = el('.hud-title');
  private readonly xpTotalEl = el('.hud-xp-total');
  private readonly xpGainEl = el('.hud-xp-gain');
  private readonly xpBarEl = el<HTMLElement>('.hud-xp-bar');
  private readonly clockEl = el('.clock-time');
  private readonly roundEl = el('.clock-round');
  private readonly seriesEl = el('.clock-series');
  private readonly magesEl = el('.clock-mages');
  private readonly alliesEl = el('.hud-allies');
  private readonly enemiesEl = el('.hud-enemies');
  private readonly shrinesEl = el('.hud-shrines');
  private readonly readiedEl = el('.spell-readied');
  private readonly slotsEl = el('.spell-slots');
  private readonly statusEl = el('.hud-status');
  private readonly crosshairEl = document.querySelector<HTMLElement>('.hud-crosshair');
  private readonly castEl = document.querySelector<HTMLElement>('.hud-cast');
  private readonly castNameEl = document.querySelector<HTMLElement>('.hud-cast-name');
  private readonly castFillEl = document.querySelector<HTMLElement>('.hud-cast-fill');
  private readonly damageEls = Array.from(
    document.querySelectorAll<HTMLElement>('.hud-damage span'),
  );
  /** Seconds left on each of the eight arcs. */
  private readonly damageFade = new Array(8).fill(0);
  private readonly logEl = el('.hud-log');
  private readonly poolEl = el('.hud-pool');
  private readonly chatLogEl = el('.chat-log');
  private readonly chatEntryEl = el('.chat-entry');
  private readonly chatScopeEl = el('.chat-scope');
  private readonly chatInputEl = el<HTMLInputElement>('.chat-input');

  /** What has been said, and how it is being said. */
  private readonly chat = makeChatLog();
  private chatScope: ChatScope = 'team';
  /** Called with a line the player wants to send. Set by main. */
  private onSay: ((scope: ChatScope, text: string) => void) | null = null;
  private readonly bannerEl = el('.hud-banner');
  private readonly noticeEl = el('.hud-notice');

  private readonly localId: string;

  constructor(localId: string) {
    this.localId = localId;
    this.bindChat();
  }

  /**
   * Fills the pause card. Called once, as soon as the local mage is known.
   *
   * Built rather than written into the page because the right-hand panel is
   * different for every class — the old prompt was a paragraph offering R to
   * raise and F to call to every player in the game, most of whom are not
   * Clerics and can do neither.
   */
  primer(me: PlayerState): void {
    const keys = (list: readonly Key[]): string => list
      .map((k) => `<li><kbd>${escapeHtml(k.press)}</kbd><span>${escapeHtml(k.does)}</span></li>`)
      .join('');

    el('#prompt-general .prompt-keys').innerHTML =
      keys(GENERAL_KEYS)
      + '<li><kbd>Dead</kbd><span>You become a ghost and fly:</span></li>'
      + keys(GHOST_KEYS);

    const name = el('.prompt-class-name');
    name.textContent = CLASSES[me.cls].name;
    name.dataset.faction = me.faction;

    const primer = PRIMERS[me.cls];
    el('#prompt-class .prompt-facts').innerHTML = primer.facts
      // Escaped first, then the one piece of markup put back — so a fact is
      // still text and only the word that is meant to be coloured is not.
      .map((fact) => `<li>${escapeHtml(fact).replace('{ley}', '<span class="prompt-ley">Ley</span>')}</li>`)
      .join('');

    el('#prompt-class .prompt-keys').innerHTML = primer.keys.length
      ? keys(primer.keys)
      : '';
  }

  /** Consumes a tick's simulation events into the log and banner. */
  ingest(events: SimEvent[], state: SimState): void {
    /*
     * Names come from the roster, falling back to the visible players.
     *
     * `players` is filtered by relevancy, so a kill between two people you had
     * never laid eyes on printed as "somebody killed somebody" — which is not
     * information withheld, it is a line of text with nothing in it. Who is
     * playing was never the secret; where they are is, and the roster carries
     * only the first.
     */
    const nameOf = (id: string): string =>
      state.roster?.find((p) => p.id === id)?.name
      ?? state.players.find((p) => p.id === id)?.name
      ?? 'someone';

    for (const ev of events) {
      switch (ev.type) {
        /*
         * A cast that could not land, and why.
         *
         * Only the local mage's own: this is feedback on a key that was just
         * pressed, and somebody else's Pain finding nobody is not news. Short,
         * because the correct response is to press it again from closer — a
         * notice that outlives the mistake becomes part of the furniture.
         */
        case 'fizzle': {
          if (ev.playerId === this.localId) this.showNotice(ev.reason);
          break;
        }
        // Only exchanges the local mage is party to. A full record of everyone
        // shooting everyone would bury the lines that need reading.
        case 'hit': {
          if (ev.amount <= 0) break;
          /*
           * Only damage arriving at you.
           *
           * Damage you deal is written in the air over whoever took it, which is
           * where you were already looking — so a line here would be the same
           * thing said twice, and the log is worth more as a place for things
           * you cannot see. Being hit is exactly that: it comes from somewhere
           * off-screen often enough to be worth a line and an arc.
           */
          if (ev.targetId === this.localId && ev.sourceId !== this.localId) {
            this.push(`${nameOf(ev.sourceId)} hit you (${ev.amount})`, 'harm');
            const me = state.players.find((x) => x.id === this.localId);
            const from = state.players.find((x) => x.id === ev.sourceId);
            if (me && from) this.markDamage(me, from.pos);
          }
          break;
        }
        case 'debuff': {
          const spell = SPELLS_BY_ID[ev.spellId];
          if (ev.sourceId === this.localId) {
            this.push(`${spell?.name ?? 'Curse'} on ${nameOf(ev.targetId)}`, 'damage');
          } else if (ev.targetId === this.localId) {
            this.push(`${nameOf(ev.sourceId)} cast ${spell?.name ?? 'a curse'} on you`, 'harm');
          }
          break;
        }
        case 'heal': {
          if (ev.amount <= 0) break;
          // Same rule. Mending you did is a green number over whoever you
          // mended; mending done *to* you is worth saying, because a Cleric
          // behind you is not something you can see.
          if (ev.targetId === this.localId && ev.sourceId !== this.localId) {
            this.push(`${nameOf(ev.sourceId)} healed you (${ev.amount})`, 'mend');
          }
          break;
        }
        case 'death':
          this.push(
            ev.killerId
              ? `${nameOf(ev.killerId)} killed ${nameOf(ev.playerId)}`
              : `${nameOf(ev.playerId)} died`,
            'kill',
          );
          if (ev.playerId === this.localId) {
            /*
             * No deadline. Being dead is a state, not an event.
             *
             * Seven seconds was long enough to read and much shorter than a
             * player often spends waiting for a Cleric — so the one line telling
             * them what they can do about it disappeared while they were still
             * doing nothing about it. It is cleared when they are alive again,
             * which is the thing that actually ends it.
             */
            this.showBanner(
              'Dead',
              'Space and Ctrl or C to fly · F to call on your shrine · or wait for a Cleric',
              Infinity,
            );
          }
          break;
        case 'raised':
          this.push(`${nameOf(ev.byId)} calls ${nameOf(ev.playerId)}'s ghost`, 'objective');
          break;
        case 'valhalla':
          // Also stateful: Valhalla lasts until they have walked out of it.
          if (ev.playerId === this.localId) {
            this.showBanner('Valhalla', 'No magic reaches here. Find the teleporter.', Infinity);
          }
          break;
        case 'respawn':
          if (ev.playerId === this.localId) this.clearBanner();
          break;
        case 'level':
          if (ev.playerId === this.localId) this.push(`You reach level ${ev.level}`, 'level');
          break;
        case 'pool': {
          const pool = state.arena.pools.find((p) => p.id === ev.poolId);
          this.push(`${pool?.name ?? 'A pool'} biased to ${ev.faction}`, 'objective');
          break;
        }
        case 'shrine':
          this.push(
            ev.conquered
              ? `The ${ev.shrineId.replace('shrine_', '')} shrine has fallen`
              : `The ${ev.shrineId.replace('shrine_', '')} shrine is restored`,
            'objective',
          );
          break;
        case 'eliminated':
          this.push(`${ev.faction} eliminated`, 'objective');
          break;
        case 'victory':
          this.showBanner(
            ev.faction ? `${ev.faction} wins` : 'Draw',
            ev.reason === 'timer' ? 'Time expired — decided on points' : 'Last shrine standing',
            Infinity,
          );
          break;
      }
    }
  }

  private push(text: string, kind: string): void {
    this.log.push({ text, kind, at: performance.now() });
    if (this.log.length > 9) this.log.shift();
  }

  private showBanner(title: string, sub: string, seconds = 4): void {
    this.banner = { title, sub };
    this.bannerUntil = seconds === Infinity ? Infinity : performance.now() + seconds * 1000;
  }

  /**
   * Refreshed rather than queued.
   *
   * Holding the key down on an out-of-range spell fires it repeatedly, and a
   * queue would turn that into the same line stuttering for several seconds
   * after they stopped. Rewriting the deadline means it simply stays up while
   * they keep trying and fades once they stop.
   */
  private showNotice(text: string, seconds = 1.4): void {
    this.notice = text;
    this.noticeUntil = performance.now() + seconds * 1000;
  }

  private clearBanner(): void {
    this.bannerUntil = 0;
  }

  /**
   * Writes a fragment only when it has actually changed.
   *
   * `render` runs every frame and most of what it draws changes once every few
   * seconds — a roster changes when somebody dies, a title when somebody levels,
   * the log when a line arrives. Assigning `innerHTML` regardless meant parsing
   * HTML, tearing down and rebuilding every node under it, and invalidating
   * style and layout for the subtree, sixty times a second for a picture that
   * was already correct. With the duplicate render loop it was a hundred and
   * twenty.
   *
   * Comparing the string first is close to free by comparison: the browser was
   * being handed the same characters anyway, so the only question is whether it
   * has to do anything with them.
   *
   * `renderStatus` already worked this way, keyed on a signature of its badges,
   * and this is the same idea generalised — with the string itself as the key,
   * so it cannot fall out of step with what the string is built from.
   */
  private writeHtml(el: HTMLElement, html: string): void {
    if (this.written.get(el) === html) return;
    this.written.set(el, html);
    el.innerHTML = html;
  }

  /** Last fragment written to each element. See `writeHtml`. */
  private readonly written = new Map<HTMLElement, string>();

  render(state: SimState): void {
    const p = state.players.find((x) => x.id === this.localId);
    if (!p) return;
    const def = CLASSES[p.cls];

    this.nameEl.textContent = p.name;
    this.writeHtml(
      this.titleEl,
      `${ordinal(p.level)} Level ${def.name}<br>`
      + `<span class="hud-faction" data-faction="${p.faction}">of ${p.faction[0].toUpperCase()}${p.faction.slice(1)}</span>`,
    );

    this.setTube('h', p.hp / Math.max(1, p.hpMax), `${p.hp}/${p.hpMax}`);
    this.setTube('f', p.fatigue / FATIGUE_MAX, `${Math.round(p.fatigue)}`);
    this.setTube('p', p.pp / Math.max(1, p.ppMax), `${p.pp}/${p.ppMax}`);
    this.setTube('l', p.ley, `${Math.round(p.ley * 100)}%`);

    if (p.xp !== this.lastXp) {
      this.sessionXp += Math.max(0, p.xp - this.lastXp);
      this.lastXp = p.xp;
    }
    this.xpTotalEl.textContent = String(p.xp);
    this.xpGainEl.textContent = `+${this.sessionXp}`;
    this.xpBarEl.style.width = `${Math.round(levelProgress(p.xp) * 100)}%`;

    /*
     * The clock, and what it is a clock for.
     *
     * During the scoreboard between rounds it counts that down instead of the
     * round length — a clock frozen at 0:00 for ten seconds says nothing, and the
     * question everybody has at that moment is how long until the next one.
     */
    /*
     * Four things the clock can be counting, and it has to say which.
     *
     * `lobby` was missing, and its absence is worth a note: a client that ends
     * up in the arena while the room is still in its lobby showed the round
     * clock — fifteen minutes, frozen, because nothing simulates in a lobby and
     * `timeRemaining` never moves. It reads exactly like a hung game, which is
     * how it was reported, and the one thing it never said was the truth.
     */
    const waiting = state.match.phase === 'lobby';
    const between = state.match.phase === 'intermission';
    const freezing = state.match.phase === 'warmup';
    const seconds = waiting ? (state.lobbyLeft ?? 0)
      : between ? state.intermission
        : freezing ? state.warmup
          : state.match.timeRemaining;
    const urgent = !between && !freezing && !waiting && state.match.timeRemaining <= 60;
    this.clockEl.textContent = formatClock(seconds);
    this.clockEl.setAttribute('data-urgent', String(urgent));
    this.clockEl.setAttribute('data-between', String(between || freezing || waiting));
    this.renderSeries(state);
    /*
     * Counted from the roster, for the same reason the enemy list is.
     *
     * `players` is filtered by relevancy, so the headcount rose and fell with
     * whoever happened to be in sight — a number that changes because you turned
     * around is worse than no number, because it looks like people leaving.
     *
     * Valhalla counts as out of the fight: a mage walking the corridor is alive
     * and not a ghost, but they are not somebody you can meet.
     */
    const living = (state.roster ?? rosterOf(state.players))
      .filter((x) => x.alive && !x.ghost && !x.inValhalla).length;
    this.magesEl.textContent = `${living} ${living === 1 ? 'mage' : 'mages'}`;

    this.renderShrines(state);
    this.renderRoster(state);
    this.renderSpells(state);
    this.renderStatus(state);
    this.renderReticle(p);
    this.renderCast(p);
    this.renderLog();
    /*
     * A banner that waits for a state to end, rather than a clock.
     *
     * `Dead` and `Valhalla` are put up without a deadline, so something has to
     * take them down — and the thing that ends both is being alive and in the
     * arena again. Doing it here rather than on a `raised` or `respawn` event
     * covers every way back: called by a Cleric, resurrected at a shrine, or
     * simply walking out of Valhalla, which raises no event at all.
     */
    if (p && p.alive && !p.ghost && !p.inValhalla && this.bannerUntil === Infinity) {
      this.clearBanner();
    }

    this.renderPool(state, p);
    this.renderBanner();
    this.renderNotice();
  }

  /**
   * Looked up once and kept.
   *
   * Four tubes, three `querySelector` calls each, every frame — twelve document
   * traversals a frame to find four pairs of elements that were in the markup
   * before the match started and will be there when it ends. Cheap individually
   * and not free, and the sort of thing that is only ever noticed in aggregate.
   */
  private readonly tubes = new Map<string, { fill: HTMLElement | null; label: HTMLElement | null }>();

  /**
   * Which round this is, and the set score.
   *
   * Three numbers rather than a winner, because with three factions the set
   * score is the only thing that says what the round about to start is *for* —
   * a side on two of three is playing a match point and everybody should be able
   * to see that without being told.
   *
   * Absent entirely from a single-round lobby. A row of zeroes under a "round 1
   * of 1" is furniture.
   */
  private renderSeries(state: SimState): void {
    const series = state.series;
    if (!series) {
      /*
       * No set, but still a freeze. A single match gets no round count and no
       * score — both would be furniture — but "round starts in" is the only
       * thing telling somebody why the clock says ten seconds.
       */
      const waitingRoom = state.match.phase === 'warmup' || state.match.phase === 'lobby';
      this.roundEl.hidden = !waitingRoom;
      this.seriesEl.hidden = true;
      if (waitingRoom) this.writeHtml(this.roundEl, this.roundLabel(state, ''));
      return;
    }

    this.roundEl.hidden = false;
    this.seriesEl.hidden = false;

    const round = Math.min(series.played + 1, series.bestOf);
    this.writeHtml(this.roundEl, this.roundLabel(state, `Round ${round} of ${series.bestOf}`));

    this.writeHtml(
      this.seriesEl,
      (['order', 'balance', 'chaos'] as const)
        .map((f) => `<span class="series-win" data-faction="${f}">${series.wins[f]}</span>`)
        .join(''),
    );
  }

  /**
   * What the clock is counting, said above it.
   *
   * The number changes meaning three times in a match — round length, scoreboard,
   * pre-round freeze — and a clock that silently swaps from 15:00 to 0:10 is a
   * clock somebody reads as a match about to end. The label carries the meaning
   * so the digits do not have to.
   */
  private roundLabel(state: SimState, playing: string): string {
    if (state.match.phase === 'lobby') return 'Waiting in lobby';
    if (state.match.phase === 'warmup') {
      /*
       * Waiting is not the same as counting down, and a clock that sits at 0:10
       * without moving looks broken rather than patient. A host who opens a
       * lobby and stands in it for a minute before a friend arrives should be
       * told that is what is happening.
       */
      return this.sidesPresent(state) < 2 ? 'Waiting for players' : 'Round starts in';
    }
    if (state.match.phase === 'intermission') return 'Next round';
    return playing;
  }

  /** How many sides have anybody on them, which is what holds a round back. */
  private sidesPresent(state: SimState): number {
    return new Set(state.players.map((p) => p.faction)).size;
  }

  private setTube(res: string, ratio: number, value: string): void {
    let parts = this.tubes.get(res);
    if (!parts) {
      const tube = document.querySelector<HTMLElement>(`.tube[data-res="${res}"]`);
      if (!tube) return;
      parts = {
        fill: tube.querySelector<HTMLElement>('.tube-fill'),
        label: tube.querySelector<HTMLElement>('.tube-value'),
      };
      this.tubes.set(res, parts);
    }
    const height = `${Math.max(0, Math.min(1, ratio)) * 100}%`;
    // Assigning a style that already holds that value still invalidates layout
    // in some engines, and these change far less often than they are written.
    if (parts.fill && parts.fill.style.height !== height) parts.fill.style.height = height;
    if (parts.label && parts.label.textContent !== value) parts.label.textContent = value;
  }

  private renderShrines(state: SimState): void {
    if (this.shrinesEl.children.length === 0) {
      for (const f of FACTIONS) {
        const pip = document.createElement('div');
        pip.className = 'shrine-pip';
        pip.dataset.faction = f;
        pip.innerHTML =
          `<div class="shrine-pip-bar"><div class="shrine-pip-fill"></div></div>` +
          `<div class="shrine-pip-label">${f.slice(0, 3)}</div>`;
        this.shrinesEl.appendChild(pip);
      }
    }
    for (const [i, f] of FACTIONS.entries()) {
      const pip = this.shrinesEl.children[i] as HTMLElement;
      const shrine = state.arena.shrines.find((s) => s.faction === f);
      const fill = pip.querySelector<HTMLElement>('.shrine-pip-fill');
      if (!shrine || !fill) continue;
      fill.style.width = `${(shrine.power / SHRINE_POWER_MAX) * 100}%`;
      pip.dataset.conquered = String(shrine.conquered);
    }
  }

  /**
   * Two lists, and deliberately not the same list twice.
   *
   * Twenty-four mages will not fit down one side of a screen, but the split is
   * not only about room. Your own side's health is actionable — it says who a
   * Cleric should be walking toward and who is about to need one. The enemy's
   * would be telling you something you are supposed to have to look for, so
   * their column is a headcount: who is playing, and nothing else.
   */
  private renderRoster(state: SimState): void {
    const me = state.players.find((p) => p.id === this.localId);
    if (!me) return;

    // Enough to sort either a player or a roster entry: both carry all three.
    const rank = (
      a: { faction: Faction; level: number; name: string },
      b: { faction: Faction; level: number; name: string },
    ): number =>
      FACTIONS.indexOf(a.faction) - FACTIONS.indexOf(b.faction)
      || b.level - a.level
      || a.name.localeCompare(b.name);

    const allies = state.players.filter((p) => p.faction === me.faction).sort(rank);
    /*
     * From the roster, not from `players`.
     *
     * `players` is filtered by relevancy, so an enemy you have not seen is not
     * in it — which meant the enemy list filled in as people wandered into view
     * and emptied again when they left. A scoreboard that changes length
     * depending on where you are looking is not a scoreboard. The roster carries
     * identity only, so showing all of it gives nothing away about position.
     */
    const enemies = (state.roster ?? rosterOf(state.players))
      .filter((p) => p.faction !== me.faction)
      .sort(rank);

    this.writeHtml(
      this.alliesEl,
      allies.map((p) => this.allyRow(p, p.id === me.id)).join(''),
    );
    /*
     * A side that cannot come back is greyed out entirely.
     *
     * Losing a shrine is the decisive moment of a match and the enemy column is
     * where it should be legible: every mage on that side is now killable for
     * good, which changes who is worth chasing and which fight is worth taking.
     * Reading that off a jewel across the arena is not something a player should
     * have to do while being shot at.
     *
     * Marked out rather than removed. Which names are on the board is still
     * information — a doomed side with six mages left is a very different
     * problem from a doomed side with one — and a list that shortens as people
     * die tells you far less than one that goes grey.
     */
    const doomed = new Set(
      FACTIONS.filter((f) => f !== me.faction && !canFactionRespawn(state.arena, f)),
    );

    this.writeHtml(this.enemiesEl, enemies
      .map((p) => (
        `<div class="roster-row" data-faction="${p.faction}"`
        + ` data-doomed="${doomed.has(p.faction)}">`
        + `<span class="roster-name">${escapeHtml(p.name)}</span>`
        + `<span class="roster-meta">${badge(p)}</span>`
        + '</div>'
      ))
      .join(''));
  }

  // ── Chat ──────────────────────────────────────────────────────────────────

  /**
   * Whether the chat line has focus, and so whether movement keys are letters.
   *
   * The controller asks this every frame. Without it, typing "well played" walks
   * you into a wall and fires two spells, which is the oldest bug in multiplayer
   * games and the one players find in the first thirty seconds.
   */
  get typing(): boolean {
    /*
     * Visible *and* holding the keyboard.
     *
     * Asking only whether the entry was on screen was a way to lose the game
     * entirely. If the line lost focus while open — and something in Valhalla
     * managed it — then Escape went to the document instead of to the input, the
     * input's own handler never ran, and the entry stayed up for ever. From
     * there the controller swallowed every keystroke because somebody was
     * "typing", and Enter was ignored for the same reason, so there was no key
     * left that could put it right. The mage simply stood there.
     *
     * Tying this to focus makes that impossible rather than unlikely: the moment
     * the line is not the thing receiving keys, the game takes them back.
     */
    return !this.chatEntryEl.hidden && document.activeElement === this.chatInputEl;
  }

  /** Where a line the player writes should go. */
  onChat(send: (scope: ChatScope, text: string) => void): void {
    this.onSay = send;
  }

  /**
   * Opens the chat line in a given scope.
   *
   * Refused rather than silently downgraded when general is closed: a player who
   * pressed the general-chat key during a match should be told the channel is
   * shut, not have their message quietly delivered to their own team, who will
   * be confused by it.
   */
  openChat(scope: ChatScope, phase: MatchPhase): void {
    if (!canSpeak(scope, phase)) {
      this.push('General chat is closed while the match is live', 'notice');
      return;
    }
    this.chatScope = scope;
    this.chatScopeEl.textContent = scope === 'team' ? 'Team' : 'All';
    this.chatEntryEl.hidden = false;
    this.chatInputEl.value = '';
    this.chatInputEl.focus();
  }

  closeChat(): void {
    this.chatEntryEl.hidden = true;
    this.chatInputEl.value = '';
    this.chatInputEl.blur();
  }

  /** Wires the line's own keys. Enter sends, Escape gives up. */
  private bindChat(): void {
    /*
     * A way out that does not depend on the line having focus.
     *
     * The belt to the focus check's braces. Escape reaches the document whatever
     * has focus, so this can always close the line — and it is a capture-phase
     * listener so it runs before anything that might swallow the key.
     */
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && !this.chatEntryEl.hidden) this.closeChat();
    }, true);

    /*
     * Taking the pointer back means playing, not typing.
     *
     * Clicking into the game while a chat line is open is unambiguous about
     * which of the two the player wants, and leaving the line up would hand
     * their movement keys to a text box.
     */
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement) this.closeChat();
    });

    this.chatInputEl.addEventListener('keydown', (ev) => {
      // Stopped here so a keystroke meant for the line never also reaches the
      // controller, which is watching the same keyboard.
      ev.stopPropagation();

      if (ev.key === 'Escape') {
        this.closeChat();
        return;
      }
      if (ev.key !== 'Enter') return;

      const text = sanitiseChat(this.chatInputEl.value);
      if (text && this.onSay) this.onSay(this.chatScope, text);
      this.closeChat();
    });
  }

  /**
   * Records something said and redraws the log.
   *
   * Filtered on receipt as well as on send, because the two are different
   * moments: a message sent to everybody as the match ended should still be
   * readable a second later, and one sent to a team must never become readable
   * because the match has since finished.
   */
  hear(msg: ChatMessage, listener: PlayerState): void {
    if (!canHear(msg, listener.faction)) return;
    this.chat.messages.push(msg);
    if (this.chat.messages.length > CHAT_SHOWN) this.chat.messages.shift();
    this.drawChat();
  }

  private drawChat(): void {
    this.chatLogEl.innerHTML = this.chat.messages
      .map((m) => (
        `<div class="chat-line" data-scope="${m.scope}">`
        + (m.scope === 'team' ? '<span class="chat-tag">[team]</span>' : '')
        + `<span class="chat-name" data-faction="${m.faction}">`
        + `${escapeHtml(m.fromName)}</span>: `
        // The one place in the interface where the content is somebody else's.
        + escapeHtml(m.text)
        + '</div>'
      ))
      .join('');
  }

  /**
   * One teammate: name, rank, and either a health bar or the fact that they are
   * down.
   *
   * A bar rather than a number because the question a teammate is answering is
   * "how bad is it", which a shape answers faster than two digits do — and it is
   * the same green as the player's own tube, so the reading transfers.
   */
  private allyRow(p: PlayerState, isSelf: boolean): string {
    const down = !p.alive || p.ghost;
    const state = p.inValhalla ? 'valhalla' : p.ghost ? 'ghost' : 'alive';
    const health = down
      ? `<span class="ally-dead">${p.inValhalla ? 'VALHALLA' : 'DEAD'}</span>`
      : `<span class="ally-bar"><span class="ally-fill" style="width:${
        Math.round((p.hp / Math.max(1, p.hpMax)) * 100)}%"></span></span>`;

    return (
      `<div class="roster-row" data-faction="${p.faction}" data-state="${state}"`
      + ` data-self="${isSelf}">`
      + `<span class="roster-name">${escapeHtml(p.name)}</span>`
      + `<span class="roster-meta">${badge(p)}</span>`
      + health
      + '</div>'
    );
  }

  private renderSpells(state: SimState): void {
    const p = state.players.find((x) => x.id === this.localId);
    if (!p) return;

    /*
     * The bar is the loadout plus whatever the class carries innately.
     *
     * A Cleric therefore shows eight tiles and a Mentalist seven, which is why
     * they get drawn narrower: the bar keeps its width and the tiles share it,
     * so the six-slot classes are unchanged and nothing shifts under the eye
     * when a Cleric respawns.
     *
     * The innates are on the right, past the numbered slots, and answer to their
     * own keys — binding them to 7 and 8 would fight six slots' worth of muscle
     * memory for no gain.
     */
    const innates = innatesFor(p.cls);
    const tiles: { spell: SpellDef; index: number; key: string }[] = [
      ...p.loadout.map((id, i) => ({ spell: SPELLS_BY_ID[id], index: i, key: String(i + 1) })),
      ...innates.map((spell) => ({ spell, index: -1, key: INNATE_KEY[spell.id] ?? '' })),
    ].filter((t) => t.spell);

    if (this.slotsEl.children.length !== tiles.length) {
      this.slotsEl.innerHTML = tiles
        .map(
          (t) =>
            `<div class="spell-slot plate"><div class="spell-slot-cd"></div>` +
            `<div class="spell-slot-gcd"></div>` +
            `<span class="spell-slot-key">${t.key}</span>` +
            `<span class="spell-slot-cost"></span>` +
            `<span class="spell-slot-name"></span></div>`,
        )
        .join('');
      this.slotsEl.dataset.count = String(tiles.length);
    }

    tiles.forEach((tile, position) => {
      const { spell } = tile;
      const i = tile.index;
      const slot = this.slotsEl.children[position] as HTMLElement;
      if (!spell || !slot) return;
      if (i < 0) {
        this.renderInnate(p, spell, slot);
        return;
      }
      // Per slot, not per player: the six slots unlock on six schedules now, so
      // a mage can be holding a tier III Primary beside an Area they cannot cast.
      const tier = tierInSlot(p, spell.slot);
      const locked = tier <= 0;
      slot.dataset.locked = String(locked);

      const nameEl = slot.querySelector<HTMLElement>('.spell-slot-name');
      const cdEl = slot.querySelector<HTMLElement>('.spell-slot-cd');
      if (nameEl) {
        nameEl.textContent = locked
          ? displayName(spell, 1, p.slotElement[i] ?? 0)
          : slotLabel(spell, tier, p.slotElement[i] ?? 0);
      }
      // The Tab hint belongs on the slot that answers to it, not in a legend.
      slot.dataset.tunable = String(Boolean(spell.elementChoices));
      const cd = p.cooldowns[i] ?? 0;
      if (cdEl) cdEl.style.height = `${Math.min(1, cd / Math.max(0.001, spell.cooldown)) * 100}%`;

      // Drawn on every slot at once, because the global cooldown locks all six.
      const gcdEl = slot.querySelector<HTMLElement>('.spell-slot-gcd');
      if (gcdEl) {
        const left = Math.max(0, p.gcd) / GLOBAL_COOLDOWN;
        gcdEl.style.transform = `scaleX(${left})`;
        gcdEl.style.opacity = left > 0 ? '0.75' : '0';
      }

      // Told apart on purpose. Away from friendly ley a Cleric's power comes
      // back slower than their own cooldowns run out — Bleeding is ready again
      // in four seconds and affordable again in sixteen — so a slot dimmed for
      // both reasons at once reads as a cooldown that lasts far longer than the
      // bar says it does.
      const cost = spell.ppCost[Math.max(0, Math.min(spell.ppCost.length - 1, tier - 1))];
      const poor = !locked && p.pp < cost;
      slot.dataset.selected = String(i === p.selectedSlot);
      slot.dataset.ready = String(!locked && cd <= 0 && p.gcd <= 0 && !poor);
      slot.dataset.poor = String(poor && cd <= 0);

      // A locked slot shows the level it arrives at rather than its price, which
      // is the only number that would help.
      const costEl = slot.querySelector<HTMLElement>('.spell-slot-cost');
      if (costEl) {
        costEl.textContent = locked
          ? `L${slotUnlockLevel(spell.slot)}`
          : poor ? String(cost) : '';
      }
    });

    // Only ever a numbered slot: the innates are pressed, not readied, so there
    // is no state in which one of them is "the spell you are holding".
    const readied = SPELLS_BY_ID[p.loadout[p.selectedSlot] ?? ''];
    const readiedTier = readied ? tierInSlot(p, readied.slot) : 0;
    this.readiedEl.textContent = !readied
      ? ''
      : readiedTier <= 0
        ? `${displayName(readied, 1)} — level ${slotUnlockLevel(readied.slot)}`
        : slotLabel(readied, readiedTier, p.slotElement[p.selectedSlot] ?? 0);
    this.readiedEl.dataset.tunable = String(Boolean(readied?.elementChoices));
  }

  /**
   * What is currently on the local mage. Rebuilt only when the set of effects
   * changes; the countdowns are updated in place, so a badge does not flicker
   * sixty times a second while it runs down.
   */
  /**
   * The reticle, sized from the cone the simulation will actually scatter within.
   * Converted through the camera's own field of view, so the gap on screen is
   * the angular error at the distance the player is looking.
   */
  private renderReticle(p: PlayerState): void {
    if (!this.crosshairEl) return;
    // 16px at the tightest, opening out with the cone. The scale is empirical:
    // it should read as a clear difference between walking and running without
    // the running reticle swallowing what it is aimed at.
    const gap = 3 + p.spread * 190;
    this.crosshairEl.style.setProperty('--gap', `${gap.toFixed(2)}px`);
  }

  /** The wind-up bar, and the name of what is winding up. */
  private renderCast(p: PlayerState): void {
    if (!this.castEl) return;
    const cast = p.casting;
    this.castEl.dataset.visible = String(Boolean(cast));
    if (!cast) return;

    const spell = SPELLS_BY_ID[cast.spellId];
    if (this.castNameEl && spell) {
      this.castNameEl.textContent = displayName(spell, tierInSlot(p, spell.slot));
    }
    if (this.castFillEl) {
      const done = 1 - Math.max(0, cast.remaining) / Math.max(0.001, cast.total);
      this.castFillEl.style.width = `${done * 100}%`;
    }
  }

  /**
   * Fades the damage arcs. Held at full for a moment and then dropped, so a
   * glancing hit still registers rather than flickering past.
   */
  tickIndicators(dt: number): void {
    for (let i = 0; i < this.damageFade.length; i++) {
      if (this.damageFade[i] <= 0) continue;
      this.damageFade[i] = Math.max(0, this.damageFade[i] - dt);
      const el = this.damageEls[i];
      if (el) el.style.opacity = String(Math.min(1, this.damageFade[i] / 0.4) * 0.85);
    }
  }

  /**
   * Lights the arc the hit came through.
   *
   * Worked out relative to where the player is looking rather than to the world,
   * because the useful answer is "behind you", not "north-east".
   */
  private markDamage(p: PlayerState, from: { x: number; z: number }): void {
    const dx = from.x - p.pos.x;
    const dz = from.z - p.pos.z;
    if (Math.abs(dx) < 0.01 && Math.abs(dz) < 0.01) return;

    /*
     * Bearing of the hit, minus the bearing the player is facing.
     *
     * `atan2(dx, -dz)` is a compass bearing measured clockwise from −Z, which is
     * the direction yaw zero looks along. The *facing* in those same terms is
     * −yaw, not yaw: forward is `(−sin yaw, −cos yaw)`, so a mage turning by a
     * positive yaw sweeps anticlockwise on a compass. Subtracting yaw rather
     * than adding it therefore turns the arc the wrong way, and turns it twice
     * as far as it should — which is why a hit from straight ahead was reported
     * as coming from behind the moment the player was not facing north, and why
     * it looked fine in whatever direction it was first tried.
     */
    const relative = Math.atan2(dx, -dz) + p.yaw;
    const eighth = Math.round(relative / (Math.PI / 4));
    const index = ((eighth % 8) + 8) % 8;

    this.damageFade[index] = 1.1;
    const el = this.damageEls[index];
    if (el) el.style.opacity = '0.85';
  }

  /**
   * One innate tile.
   *
   * Close to a spell slot but not the same: it is never *selected*, so it never
   * shows as the readied spell, and Flight has no cooldown to sweep because it
   * is paid for continuously while held rather than bought once.
   */
  private renderInnate(p: PlayerState, spell: SpellDef, slot: HTMLElement): void {
    const tier = tierForSpell(spell, p.level);
    const locked = tier <= 0;
    slot.dataset.locked = String(locked);
    slot.dataset.innate = 'true';
    slot.dataset.selected = 'false';
    slot.dataset.tunable = 'false';

    const nameEl = slot.querySelector<HTMLElement>('.spell-slot-name');
    const cdEl = slot.querySelector<HTMLElement>('.spell-slot-cd');
    const costEl = slot.querySelector<HTMLElement>('.spell-slot-cost');

    if (spell.kind === 'flight') {
      // Held, not cast. The tile says whether it is available and whether it is
      // happening, which is all there is to say about it.
      if (nameEl) nameEl.textContent = spell.name;
      if (cdEl) cdEl.style.height = '0%';
      if (costEl) costEl.textContent = '';
      slot.dataset.ready = String(!p.drained && !p.exhausted);
      slot.dataset.poor = String(p.drained);
      slot.dataset.active = String(p.flying);
      return;
    }

    const cooldown = spell.id === 'call_ghost' ? p.callCooldown : p.raiseCooldown;
    const cost = spell.ppCost[Math.max(0, tier - 1)];
    const poor = !locked && p.pp < cost;

    if (nameEl) nameEl.textContent = locked ? spell.name : slotLabel(spell, tier);
    if (cdEl) {
      cdEl.style.height = `${Math.min(1, cooldown / Math.max(0.001, spell.cooldown)) * 100}%`;
    }
    if (costEl) {
      costEl.textContent = locked
        ? `L${spell.unlockLevels?.[0] ?? 1}`
        : poor ? String(cost) : '';
    }

    slot.dataset.ready = String(!locked && cooldown <= 0 && p.gcd <= 0 && !poor);
    slot.dataset.poor = String(poor && cooldown <= 0);
    slot.dataset.active = 'false';
  }

  private renderStatus(state: SimState): void {
    const p = state.players.find((x) => x.id === this.localId);
    if (!p) {
      this.statusEl.innerHTML = '';
      return;
    }

    const badges = badgesFor(p.effects);
    const signature = badges.map((b) => b.key).join('|');
    if (signature !== this.statusSignature) {
      this.statusSignature = signature;
      this.statusEl.innerHTML = badges
        .map(
          (b) =>
            `<div class="status-badge" data-key="${b.key}" data-tone="${b.tone}"`
            + `${b.element ? ` data-element="${b.element}"` : ''} title="${b.label}">`
            + `<svg viewBox="0 0 24 24" aria-hidden="true">${b.svg}</svg>`
            + `<div class="status-life"><div class="status-life-bar"></div></div>`
            + `<span class="status-label">${b.label}</span>`
            + `</div>`,
        )
        .join('');
    }

    badges.forEach((b, i) => {
      const node = this.statusEl.children[i] as HTMLElement | undefined;
      const bar = node?.querySelector<HTMLElement>('.status-life-bar');
      if (bar) bar.style.width = `${b.fraction * 100}%`;
      // Running out is worth noticing before it happens.
      if (node) node.dataset.fading = String(b.seconds <= 2);
    });
  }

  private renderLog(): void {
    const now = performance.now();
    this.log = this.log.filter((l) => now - l.at < 7000);
    this.writeHtml(
      this.logEl,
      this.log
        .map((l) => `<div class="log-line" data-kind="${l.kind}">${l.text}</div>`)
        .join(''),
    );
  }

  private renderNotice(): void {
    const visible = performance.now() < this.noticeUntil;
    this.noticeEl.setAttribute('data-visible', String(visible));
    if (visible && this.noticeEl.textContent !== this.notice) {
      this.noticeEl.textContent = this.notice;
    }
  }

  /**
   * What the pool underfoot is, and who is taking it.
   *
   * Shown only while standing in one, because outside a pool it is a fact about
   * somewhere else — and a HUD that says things about somewhere else is a HUD
   * people stop reading. A ghost sees nothing either: they cannot bias anything,
   * and the readout is about what *you* are doing.
   */
  private renderPool(state: SimState, me: PlayerState | undefined): void {
    if (!me || !isEmbodied(me)) {
      this.poolEl.hidden = true;
      return;
    }

    const pool = poolUnderfoot(state.arena, me);
    if (!pool) {
      this.poolEl.hidden = true;
      return;
    }

    const said = poolReadout(pool, me, state.players);
    this.poolEl.hidden = false;
    this.poolEl.textContent = said.text;
    this.poolEl.dataset.tone = said.tone;
  }

  private renderBanner(): void {
    const visible = performance.now() < this.bannerUntil;
    this.bannerEl.setAttribute('data-visible', String(visible));
    if (visible) {
      this.bannerEl.innerHTML =
        `${this.banner.title}<span class="hud-banner-sub">${this.banner.sub}</span>`;
    }
  }
}
